import React from 'react';

import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { useI18nContext } from '@bingads-webui/copilot-i18n';
import { useAdsCopilotPilotMap } from '@bingads-webui/ads-copilot-ui';
import { CopilotWelcomeTileList, ICopilotWelcomeTileListProps } from '@bingads-webui/copilot-welcome-tiles';
import { IQuestion, ISiteMapNode } from '@bingads-webui/search-utils';
import { CopilotIcon } from '@bingads-webui-react/copilot-ui-base';
import { MSACopilotDisclaimer } from './MSACopilotDisclaimer';
import { withQuestions } from './hooks';

const useDisclaimerStyles = makeStyles({
  introduction: {
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
    marginLeft: tokens.spacingHorizontalXXL,
    marginRight: tokens.spacingHorizontalXXL,
    marginBottom: tokens.spacingVerticalXL,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
  logo: {
    display: 'inline',
  },
  topBlock: {
    textAlign: 'center',
    lineHeight: tokens.lineHeightBase500,
    marginTop: '28px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase500,
    textAlign: 'center',
    fontWeight: tokens.fontWeightBold,
    display: 'inline',
  },
  subtitle: {
    color: '#444444',
    textAlign: 'center',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    lineHeight: tokens.lineHeightBase200,
    marginBottom: tokens.spacingVerticalXXL,
  },
  welcomeTileList: {
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
  },
  disclaimer: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalL,
    textAlign: 'center',
  },
});

export type MSACopilotConversationTopProps = {
  onWelcomeTileClick?: ICopilotWelcomeTileListProps['onClick'];
  onRenderWelcomeTile?: (question: { text: string, category: string }) => void,
  questions?: IQuestion[],
  isUxImprovementsEnabled?: boolean,
};

export const MSAdsCopilotConversationTop: React.FC<MSACopilotConversationTopProps> = React.memo((props) => {
  const classes = useDisclaimerStyles();
  const {
    onWelcomeTileClick,
    onRenderWelcomeTile,
    questions,
    isUxImprovementsEnabled: isUxImprovementsEnabledProp,
  } = props;

  const i18n = useI18nContext();
  const {
    isUxImprovementsEnabled: isUxImprovementsEnabledPilotMap,
  } = useAdsCopilotPilotMap();
  const isUxImprovementsEnabled = isUxImprovementsEnabledPilotMap || isUxImprovementsEnabledProp;

  return (
    <>
      {isUxImprovementsEnabled
        ? (
          <>
            <div className={classes.topBlock}>
              <CopilotIcon size="large" color="multicolor" title={i18n.getString(_TL_('Copilot icon'))} iconStyles={classes.logo} />
              <p className={classes.title}>{i18n.getString(_TL_('Copilot'))}</p>
            </div>
            <p className={classes.subtitle}>
              {i18n.getString(_TL_('Your AI assistant for advertising'))}
            </p>
          </>
        )
        : (
          <p className={classes.introduction}>
            {i18n.getString(_TL_('Ask Copilot in Microsoft Advertising Platform to help with:'))}
          </p>
        ) }
      <CopilotWelcomeTileList
        className={classes.welcomeTileList}
        onClick={onWelcomeTileClick}
        onRenderWelcomeTile={onRenderWelcomeTile}
        questions={questions}
      />
      <p className={mergeClasses('msa-copilot-disclaimer', classes.disclaimer)}>
        <MSACopilotDisclaimer i18n={i18n} isShortDisclaimer={isUxImprovementsEnabled} />
      </p>
    </>
  );
});

export const MSAdsCopilotConversationTopWithQuestions = (props: MSACopilotConversationTopProps & { siteMap: ISiteMapNode[], isReportingInsightsEnabled?: boolean, isTroubleshootingEnabled?: boolean }) => (
  withQuestions({ ...props, Component: MSAdsCopilotConversationTop })
);
